import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dataIntegrityApi } from '../lib/data-integrity-api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { ExclusionInput, StatusUpdateInput } from '../types/data-integrity.js';

/** @graph-connects dataIntegrityApi [calls] データ整合性 API 呼出 */
export function useIntegritySummary() {
  return useQuery({
    queryKey: queryKeys.dataIntegrity.summary,
    queryFn: () => dataIntegrityApi.getSummary(),
    staleTime: 60_000,
    retry: false,
  });
}

/** @graph-connects dataIntegrityApi [calls] データ整合性 API 呼出 */
export function useCreateExclusion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ExclusionInput) => dataIntegrityApi.createExclusion(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataIntegrity.exclusions });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataIntegrity.summary });
    },
  });
}

/** @graph-connects dataIntegrityApi [calls] データ整合性 API 呼出 */
export function useRemoveExclusion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dataIntegrityApi.removeExclusion(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataIntegrity.exclusions });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataIntegrity.summary });
    },
  });
}

/** @graph-connects dataIntegrityApi [calls] データ整合性 API 呼出 */
export function useUpdateFindingStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ findingId, input }: { findingId: string; input: StatusUpdateInput }) =>
      dataIntegrityApi.updateFindingStatus(findingId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataIntegrity.summary });
    },
  });
}

/** @graph-connects dataIntegrityApi [calls] データ整合性 API 呼出 */
export function useActionLogs(findingId?: string) {
  return useQuery({
    queryKey: [...queryKeys.dataIntegrity.summary, 'action-logs', findingId ?? 'all'],
    queryFn: () => dataIntegrityApi.getActionLogs(findingId, 50),
    staleTime: 30_000,
  });
}
